import type { ReactNode } from 'react';

/**
 * Inline SVG icons used across the agent chat / pipeline UI.
 *
 * These replace the pictographic emoji the UI used to render. They are
 * feather-style line icons: a 24x24 viewBox, `stroke="currentColor"` so they
 * inherit text color, and sized in `em` so they scale with the surrounding
 * font-size. Drop one anywhere an emoji glyph used to sit.
 */

type IconProps = { size?: number | string; className?: string };

function svg(children: ReactNode, { size = '1em', className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={{ flex: '0 0 auto', verticalAlign: 'middle' }}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export const IconFolderOpen = (p: IconProps = {}) =>
  svg(<path d="M6 14l1.5-3.5A2 2 0 0 1 9.3 9H20a1 1 0 0 1 .95 1.3l-1.8 5.4A2 2 0 0 1 17.25 17H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2v1" />, p);

export const IconFolder = (p: IconProps = {}) =>
  svg(<path d="M4 20a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2z" />, p);

export const IconSearch = (p: IconProps = {}) =>
  svg(<><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>, p);

export const IconClipboard = (p: IconProps = {}) =>
  svg(<><rect x="8" y="2" width="8" height="4" rx="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><line x1="9" y1="12" x2="15" y2="12" /><line x1="9" y1="16" x2="13" y2="16" /></>, p);

export const IconCheck = (p: IconProps = {}) =>
  svg(<polyline points="20 6 9 17 4 12" />, p);

export const IconStethoscope = (p: IconProps = {}) =>
  svg(<><path d="M4.5 3v5a4 4 0 0 0 8 0V3" /><path d="M8.5 15v1a5 5 0 0 0 10 0v-3" /><circle cx="18.5" cy="10" r="2" /></>, p);

export const IconChat = (p: IconProps = {}) =>
  svg(<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />, p);

export const IconPackage = (p: IconProps = {}) =>
  svg(<><path d="M16.5 9.4 7.5 4.21" /><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></>, p);

export const IconGear = (p: IconProps = {}) =>
  svg(<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>, p);

export const IconWrench = (p: IconProps = {}) =>
  svg(<path d="M14.7 6.3a4 4 0 0 0-5.2 5.2l-6 6a2 2 0 1 0 2.8 2.8l6-6a4 4 0 0 0 5.2-5.2l-2.6 2.6-2.1-.5-.5-2.1z" />, p);

export const IconRefresh = (p: IconProps = {}) =>
  svg(<><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></>, p);

export const IconCopy = (p: IconProps = {}) =>
  svg(<><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>, p);

export const IconRocket = (p: IconProps = {}) =>
  svg(<><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" /><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" /><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" /></>, p);

/** Step-key -> icon node, used by the pipeline step rows. */
export const STEP_ICONS: Record<string, ReactNode> = {
  indexing: <IconFolderOpen />,
  retrieving: <IconSearch />,
  planning: <IconClipboard />,
  executing: <IconCheck />,
  validating: <IconCheck />,
  diagnosing: <IconStethoscope />,
  streaming: <IconChat />,
  contextGather: <IconPackage />,
  init: <IconGear />,
  respond: <IconChat />,
};
